import {AsyncReentrancyGuard} from "@cm/utils/async-reentrancy-guard"
import {UncachedEvaluator} from "#graph/evaluators/uncached-evaluator"
import {BaseGraphNode} from "#graph/nodes/base/base-graph-node"
import {GraphNode} from "#graph/nodes/graph/graph-node"

import type {Evaluator} from "#graph/evaluators/types"
import type {ParameterValue} from "#graph/nodes/graph/helpers"

export class CachedEvaluator<Context> extends UncachedEvaluator<Context> implements Evaluator<Context> {
    constructor(
        context: Context,
        disableValidation = false,
        private readonly runCache = new Map<GraphNode<unknown, Context>, unknown>(),
        private readonly asyncPromiseSerializerByNode = new Map<GraphNode<unknown, Context>, AsyncReentrancyGuard.PromiseSerializer>(),
    ) {
        super(context, disableValidation)
    }

    protected retrieveFromCache(value: GraphNode<unknown, Context>): unknown | undefined {
        return this.runCache.get(value)
    }

    override get = async <ReturnType>(value: ParameterValue<ReturnType>): Promise<ReturnType> => {
        if (value instanceof GraphNode) {
            // get async promise serializer
            let asyncPromiseSerializer = this.asyncPromiseSerializerByNode.get(value)
            if (asyncPromiseSerializer === undefined) {
                asyncPromiseSerializer = new AsyncReentrancyGuard.PromiseSerializer()
                this.asyncPromiseSerializerByNode.set(value, asyncPromiseSerializer)
            }
            return asyncPromiseSerializer.executeSequentially<ReturnType>(async () => {
                // look up cached value
                const cachedValue = this.retrieveFromCache(value) as ReturnType
                if (cachedValue !== undefined) {
                    return cachedValue
                }
                // evaluate value and store in cache
                const computedValue = (await this.evaluateGraph(value)) as ReturnType
                this.runCache.set(value, computedValue)
                return computedValue
            })
        }
        return value
    }

    override getSync = <ReturnType>(value: ParameterValue<ReturnType, Context>): ReturnType => {
        if (value instanceof BaseGraphNode) {
            // lookup cached value
            const cachedValue = this.runCache.get(value)
            if (cachedValue !== undefined) {
                return cachedValue as ReturnType
            }
            // evaluate value and store in cache
            const computedValue = this.evaluateGraphSync(value) as ReturnType
            this.runCache.set(value, computedValue)
            return computedValue
        }

        return value as ReturnType
    }

    invalidate(graphNode: GraphNode<unknown, Context>) {
        this.runCache.delete(graphNode)
        graphNode.parents.forEach((parent) => this.invalidate(parent))
    }

    override withContext = <OtherContext extends Context = Context>(context: OtherContext): Evaluator<OtherContext> => {
        return new CachedEvaluator(context, this.disableValidation, this.runCache, this.asyncPromiseSerializerByNode)
    }
}
